import { useEffect } from 'react';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { logInfo, logError, logDebug, logTrace } from './loggerService';
import { setDevicePushToken } from './deviceService';
import { useActivityStore } from '../store/useActivityStore';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';
const PUSH_DISABLED = import.meta.env.VITE_FIREBASE_PUSH_DISABLED === 'true';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const X_API_KEY = import.meta.env.VITE_X_API_KEY || '';

// Holds the FCM service worker registration set by main.tsx after a
// successful narrow-scope register('/firebase-messaging-sw.js', { scope: ... }).
// getPushToken() passes this into FirebaseMessaging.getToken() so the SDK
// doesn't try to auto-discover an SW at root — important once a separate
// PWA SW lands at `/`. Null until main.tsx finishes registration; we
// gracefully fall through to default discovery in the brief startup window.
let fcmSwRegistration: ServiceWorkerRegistration | null = null;

export function setFcmServiceWorkerRegistration(reg: ServiceWorkerRegistration): void {
  fcmSwRegistration = reg;
  logTrace('FCM service worker registration captured', undefined, {
    scope: reg.scope,
  });
}

export function getFcmServiceWorkerRegistration(): ServiceWorkerRegistration | null {
  return fcmSwRegistration;
}

/**
 * Request push notification permission.
 * Call this explicitly during onboarding — NOT on app load.
 * If granted, retrieves the FCM token.
 * Returns true if permission was granted, false otherwise.
 */
export async function requestPushPermission(): Promise<boolean> {
  if (PUSH_DISABLED) {
    logTrace('Push notifications disabled via VITE_FIREBASE_PUSH_DISABLED');
    return false;
  }
  try {
    const result = await FirebaseMessaging.requestPermissions();

    if (result.receive !== 'granted') {
      logDebug('Push permission denied by user');
      return false;
    }

    logInfo('Push permission granted');
    useActivityStore.getState().setPushPermissionGranted(true);

    const token = await getPushToken();
    if (token) {
      logTrace(`FCM token received: ${token.substring(0, 20)}...`);
      await setDevicePushToken(token);
    }

    return true;
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), 'Failed to request push permission');
    return false;
  }
}

/**
 * Get the current FCM token.
 * Returns null if permission has not been granted or token retrieval fails.
 */
export async function getPushToken(): Promise<string | null> {
  if (PUSH_DISABLED) {
    return null;
  }
  try {
    const { token } = await FirebaseMessaging.getToken({
      vapidKey: VAPID_KEY || undefined,
      // Pass the narrow-scope FCM SW registration explicitly so the SDK
      // doesn't fall back to auto-discovering an SW at `/`. Null in the
      // brief startup window before main.tsx's register() resolves; FCM's
      // own fallback handles that case.
      serviceWorkerRegistration: fcmSwRegistration ?? undefined,
    });
    return token;
  } catch (error) {
    logError(error instanceof Error ? error : new Error(String(error)), 'Failed to get FCM token');
    return null;
  }
}

/**
 * Tell the backend a push reached the open app (POST /n/{ref}/ack, status
 * `foreground`). When the app is visible, FCM hands the push to the page
 * instead of the service worker, so nothing is displayed and the worker acks
 * nothing — without this the row looks like the device never got it.
 *
 * Plain fetch, the same request the service worker sends, not apiService:
 * this is background telemetry, so a failure must never open the global error
 * modal or route to /no-network.
 */
function ackForegroundPush(ref: string | undefined) {
  if (!ref) return;
  fetch(`${API_BASE_URL}/n/${encodeURIComponent(ref)}/ack`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(X_API_KEY ? { 'X-Api-Key': X_API_KEY } : {}),
    },
    body: JSON.stringify({ status: 'foreground' }),
  }).catch((error) => {
    logDebug('Push foreground ack failed', undefined, { error: String(error) });
  });
}

/**
 * Unified push notification handler for both foreground and background.
 * @param notification - The notification payload (title, body, data)
 * @param foreground - true if received while app is active, false if user tapped OS notification
 *
 * No toast is shown here: in foreground the WS layer already toasts the same
 * event (avoiding the duplicate); in background (tap) we navigate instead via
 * PushTapNavigator, which reads `pendingPushRoute` from useActivityStore.
 */
function handlePush(
  notification: {
    title?: string;
    body?: string;
    data?: Record<string, string>;
    // Web path: when FCM's JS SDK auto-displays a `notification` payload it
    // wraps the original message under `FCM_MSG` and that becomes
    // event.notification.data. Native (Capacitor) skips this wrapping and
    // exposes the FCM data fields directly on `data`.
    FCM_MSG?: { data?: Record<string, string> };
  },
  foreground: boolean,
) {
  logDebug(`Push ${foreground ? 'foreground' : 'background'}`, undefined, notification);

  if (foreground) {
    ackForegroundPush(notification.data?.ref);
    return;
  }

  const targetUrl =
    notification.data?.targetUrl || notification.FCM_MSG?.data?.targetUrl;
  if (targetUrl) {
    useActivityStore.getState().setPendingPushRoute(targetUrl);
  }
}

/**
 * Renderless component that sets up push notification listeners on mount.
 * Does NOT request permission — call requestPushPermission() explicitly.
 * Place in App.tsx alongside InitAuth.
 */
export function InitPush() {
  useEffect(() => {
    let cancelled = false;
    const listeners: Array<() => Promise<void>> = [];

    const setup = async () => {
      if (PUSH_DISABLED) {
        logDebug('Push notifications disabled — skipping listener setup');
        return;
      }

      // Restore push permission state and token from browser
      const { receive } = await FirebaseMessaging.checkPermissions();
      if (cancelled) return;
      if (receive === 'granted') {
        useActivityStore.getState().setPushPermissionGranted(true);
        const token = await getPushToken();
        if (token && !cancelled) {
          await setDevicePushToken(token);
        }
      }
      if (cancelled) return;

      const received = await FirebaseMessaging.addListener(
        'notificationReceived',
        (event) => {
          handlePush({
            title: event.notification.title,
            body: event.notification.body,
            data: event.notification.data as Record<string, string>,
          }, true);
        },
      );
      listeners.push(() => received.remove());

      const action = await FirebaseMessaging.addListener(
        'notificationActionPerformed',
        (event) => {
          handlePush({
            title: event.notification.title,
            body: event.notification.body,
            data: event.notification.data as Record<string, string>,
          }, false);
        },
      );
      listeners.push(() => action.remove());

      // Listen for background notification clicks from service worker (web only)
      if ('serviceWorker' in navigator) {
        const swListener = (event: MessageEvent) => {
          if (event.data?.type === 'PUSH_NOTIFICATION_CLICK') {
            handlePush(event.data.notification || {}, false);
          }
        };
        navigator.serviceWorker.addEventListener('message', swListener);
        listeners.push(async () => navigator.serviceWorker.removeEventListener('message', swListener));
      }

      logDebug('Push notification listeners registered');
    };

    setup();

    return () => {
      cancelled = true;
      listeners.forEach((remove) => remove());
    };
  }, []);

  return null;
}
